import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { SharedService } from 'src/app/shareModule/services/shared.service';
import { isPlatformServer } from '@angular/common';

interface YouTubeItem {
    id: number;
    productId: number;
    youtubeUrl: string;
    createdBy: number;
    createdAt: string;
    updatedAt: string;
}

interface YouTubePagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

interface YouTubeResponse {
    data: YouTubeItem[];
    pagination: YouTubePagination;
}

@Component({
    selector: 'app-community-youtube',
    templateUrl: './community-youtube.component.html',
    styleUrls: ['./community-youtube.component.scss'],
})
export class CommunityYoutubeComponent implements OnInit, OnDestroy {
    public youtubeList: YouTubeItem[] = [];
    public pagination: YouTubePagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 6,
    };
    public isLoadingMore: boolean = false;
    public isLoading: boolean = true;
    public hasMoreItems: boolean = true;

    private destroy$ = new Subject<void>();

    constructor(
        private customerService: CustomerService,
        private sharedService: SharedService,
        private cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object
    ) { }

    ngOnInit(): void {
        this.loadYoutubeVideos();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public loadYoutubeVideos(page: number = 1, append: boolean = false): void {
        if (!append) {
            this.isLoading = true;
            this.youtubeList = [];
        } else {
            this.isLoadingMore = true;
        }

        const payload = {
            page: page,
            limit: this.pagination.itemsPerPage,
        };

        this.customerService
            .getYoutubeUrl(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    const youtubeData: YouTubeResponse = response.body;

                    if (append) {
                        this.youtubeList = [...this.youtubeList, ...youtubeData.data];
                    } else {
                        this.youtubeList = youtubeData.data;
                    }

                    this.pagination = youtubeData.pagination;
                    this.hasMoreItems = this.pagination.currentPage < this.pagination.totalPages;

                    this.isLoading = false;
                    this.isLoadingMore = false;
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Error loading YouTube videos:', error);
                    this.isLoading = false;
                    this.isLoadingMore = false;
                    this.cdr.markForCheck();
                },
            });
    }

    public loadMore(): void {
        if (!this.isLoadingMore && this.hasMoreItems) {
            this.loadYoutubeVideos(this.pagination.currentPage + 1, true);
        }
    }

    public getYouTubeThumbnail(url: string): string {
        if (!url) return '';

        // Extract video ID from YouTube URL
        const videoId = this.extractVideoId(url);
        if (!videoId) return '';

        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    public getYouTubeEmbedUrl(url: string): string {
        if (!url) return '';

        const videoId = this.extractVideoId(url);
        if (!videoId) return '';

        return `https://www.youtube.com/embed/${videoId}`;
    }

    private extractVideoId(url: string): string {
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = url.match(regex);
        return match ? match[1] : '';
    }

    public openVideoModal(url: string): void {
        if (!this.sharedService.isBrowser()) return;

        // Create a simple modal or use existing modal service
        // For now, we'll open in a new tab
        const embedUrl = this.getYouTubeEmbedUrl(url);
        if (embedUrl) {
            window.open(url, '_blank');
        }
    }

    public trackById(index: number, item: YouTubeItem): number {
        return item.id;
    }
}
